import {
  DalsiPredkladatel as DalsiPredkladatelGenerated,
  LegislativnyMaterialDetailApi,
  LegMaterialDetail,
  NovelizovanyPredpis as NovelizovanyPredpisGenerated,
  SuvisiaciProces as SuvisiaciProcesGenerated,
} from 'elegislativa-frontend-ts-axios';
import { mapStadium } from '../../model/legislativnyMaterial/StadiumMaterialu';
import LegislativnyMaterialDetail
  from '../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import apiConfiguration from '../configuration/apiConfiguration';
import { ClientResponse } from '../../model/ClientResponse';
import { mapDolezitost } from '../../model/pracovnyKrok/Dolezitost';
import { mapSubject } from '../../model/legislativnyMaterial/SubjectItem';
import { mapKategoria } from '../../model/pracovnyKrok/Kategoria';
import { elegislativaPrefixPath } from '../configuration/apiConfigurationConstants';
import Predkladatel from '../../model/legislativnyMaterial/Predkladatel';
import SuvisiaciProces from '../../model/legislativnyMaterial/SuvisiaciProces';
import { mapOrganizacia } from '../../model/ciselniky/Organizacia';
import PredpisZbierky from '../../model/legislativnyMaterial/PredpisZbierky';
import PracovnyKrokEnum from '../../model/pracovnyKrok/PracovnyKrokEnum';

const client = new LegislativnyMaterialDetailApi(apiConfiguration(elegislativaPrefixPath));

const map = (data: LegMaterialDetail): LegislativnyMaterialDetail => ({
  stadium: mapStadium(data.stadium),
  nazov: data.nazov,
  uuid: data.uuid,
  cisloLegislativnehoMaterialu: data.cisloLegislativnehoMaterialu,
  poslednaZmena: data.poslednaZmena ? new Date(data.poslednaZmena) : null,
  datumZaciatkuPkMpk: data.datumZaciatkuPkMpk ? new Date(data.datumZaciatkuPkMpk) : null,
  datumUkonceniaPkMpk: data.datumUkonceniaPkMpk ? new Date(data.datumUkonceniaPkMpk) : null,
  rezortneCislo: data.rezortneCislo,
  podnet: data.podnet,
  zodpovednaInstitucia: mapOrganizacia(data.zodpovednaInstitucia),
  aktualnaPredkladatelskaInstitucia: mapOrganizacia(data.aktualnaInstitucia),
  zodpovednyPredkladatel: mapSubject(data.zodpovednyPredkladatel),
  aktualnyPredkladatel: mapSubject(data.aktualnyPredkladatel),
  skrateneMpk: data.skrateneMpk,
  sledovany: data.sledovany,
  sablonaProcesu: data.sablonaProcesu,
  suvisiaceProcesy: data.suvisiaceProcesy?.map(proces => mapSuvisiaciProces(proces)) || [],
  dalsiPredkladatelia: data.dalsiPredkladatelia
    ?.map(dalsiPredkladatel => mapDalsiPredkladatel(dalsiPredkladatel)).sort((a, b) => Intl.Collator()
      .compare(a.predkladatel?.nazov, b.predkladatel?.nazov)) || [],
  hlavnyNovelizovanyPredpis: data.hlavnyNovelizovanyPredpis ? mapPredpisZbierky(data.hlavnyNovelizovanyPredpis)
    : undefined,
  novelizovanePredpisy: data.novelizovanePredpisy?.map(predpis => mapPredpisZbierky(predpis)) || [],
  typ: data.typ,
  oblastList: data.oblastList,
  dovodSkratenehoMpk: data.dovodSkratenehoMpk,
  dovodZruseniaMpk: data.dovodZruseniaMpk,
  vysledokNrSr: data.vysledokNrSr,
  dovodUkoncenia: data.dovodUkoncenia,
  aktEu: data.aktEu,
  stupenDolezitostiAktu: mapDolezitost(data.stupenDolezitostiAktu),
  gestor: mapOrganizacia(data.gestor),
  spolugestori: data?.spolugestorList?.map((org) => mapOrganizacia(org))
    .sort((a, b) => Intl.Collator()
      .compare(a.nazov, b.nazov)) || undefined,
  kategoriaAktu: mapKategoria(data.kategoriaAktu),
  dovodOdmietnutiaGestora: data.dovodOdmietnutiaGestora,
  namietkaKuGestorstvu: data.namietkaKuGestorstvu,
  vlastnikAktivity: data.vlastnikAktivity ? mapOrganizacia(data.vlastnikAktivity) : null,
  hasOpravneniePracovneKroky: data.opravneniePracovneKroky,
  hasOpravnenieSprievodneDokumenty: data.opravnenieSprievodneDokumenty,
  hasPracovnyKrokInZasobnikPrace: data.pracovnyKrokInZasobnikPrace,
  autorProcesu: mapSubject(data.autorProcesu),
  camundaId: data.camundaId,
  poradnyOrganVladyList: data.poradnyOrganVladyList?.map(pov => mapOrganizacia(pov)).map(pov => mapOrganizacia(pov))
    .sort((a, b) => Intl.Collator()
      .compare(a.nazov, b.nazov)) || undefined,
  dalsiPripomienkovatelia: data.dalsiPripomienkovatelia?.map(pov => mapOrganizacia(pov))
    .sort((a, b) => Intl.Collator()
      .compare(a.nazov, b.nazov)) || undefined,
  vyhlasenyPodCislomList: data.vyhlasenyPodCislomList?.map(predpis => mapPredpisZbierky(predpis)) || [],
  datumVytvoreniaProcesu: data.datumVytvoreniaProcesu ? new Date(data.datumVytvoreniaProcesu) : null,
});

const mapDalsiPredkladatel = (dalsiPredkladatel: DalsiPredkladatelGenerated): Predkladatel => ({
  predkladatel: mapSubject(dalsiPredkladatel.predkladatel),
  organizacia: mapOrganizacia(dalsiPredkladatel.organizacia),
});

const mapSuvisiaciProces = (generated: SuvisiaciProcesGenerated): SuvisiaciProces => ({
  cisloLp: generated.cisloLp,
  nazov: generated.nazov,
  cisloLpNazov: `${generated.cisloLp} - ${generated.nazov}`,
});

const mapPredpisZbierky = (generated: NovelizovanyPredpisGenerated): PredpisZbierky => ({
  iri: generated.iri,
  menovka: generated.menovka,
  popis: generated.popis,
  redirectUrl: `/ezbierky/pravne-predpisy${generated.iri}`,
});

// eslint-disable-next-line import/prefer-default-export
export const fetchLegislativnyMaterialDetail = async (
  type: string,
  year: number,
  index: number,
  pracovnyKrok: PracovnyKrokEnum,
  headers?: object,
):
    Promise<ClientResponse<LegislativnyMaterialDetail>> => {
  const {
    data,
    status,
  } = await client.getLegMaterialDetail(type, year, index, pracovnyKrok, headers);
  return {
    data: map(data),
    responseCode: status,
  };
};
